import React, { useState, useEffect } from 'react';
import {
  signInWithPopup,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  getAdditionalUserInfo,
} from 'firebase/auth';
import { auth, googleProvider, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { LogOut } from 'lucide-react';
import { API_BASE_URL } from '../../apiConfig';
import analytics from '../../utils/analytics';
import { apiFetch } from '../../api/authHeaders';
import { Alert } from '../ui';

/**
 * Do NOT use signInWithRedirect while authDomain is *.firebaseapp.com and the
 * SPA is on criticai.ca (GitHub Pages). Safari/Brave block the cross-site
 * storage handoff, so users return from Google still signed out.
 * Popup from a direct user tap works; redirect needs same-origin /__/auth
 * (Firebase Hosting custom authDomain or a reverse proxy).
 */

const GoogleSignup = ({ onAuthChange }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const getUserInitial = (user) => {
    if (!user) return { initial: '?', gradient: 'from-apple-blue to-apple-purple' };
    const name = user.name || user.email || '';
    const initial = name.charAt(0).toUpperCase();

    const gradients = [
      'from-apple-blue to-apple-purple',
      'from-apple-green to-apple-teal',
      'from-apple-purple to-apple-pink',
      'from-apple-orange to-apple-yellow',
      'from-apple-blue to-apple-teal',
    ];
    const index = initial.charCodeAt(0) % gradients.length;
    return { initial, gradient: gradients[index] };
  };

  const syncBackendAndSubscription = async (firebaseUser) => {
    const userData = {
      id: firebaseUser.uid,
      email: firebaseUser.email,
      name: firebaseUser.displayName,
      picture: firebaseUser.photoURL,
      provider: 'firebase-google',
    };

    try {
      await apiFetch(`${API_BASE_URL}/api/auth/sync-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
    } catch (error) {
      console.error('Failed to sync user with backend:', error);
    }

    try {
      if (firebaseUser.email) {
        const subscriptionDoc = await getDoc(
          doc(db, 'subscriptions', firebaseUser.email.toLowerCase())
        );
        if (subscriptionDoc.exists()) {
          const subscriptionData = subscriptionDoc.data();
          if (subscriptionData.subscriptionId) {
            localStorage.setItem('criticai_subscription_id', subscriptionData.subscriptionId);
          }
          if (subscriptionData.customerId) {
            localStorage.setItem('criticai_subscription_customer_id', subscriptionData.customerId);
          }
          if (subscriptionData.currentPeriodEnd) {
            localStorage.setItem(
              'criticai_subscription_period_end',
              subscriptionData.currentPeriodEnd
            );
          }
          localStorage.setItem('criticai_subscription', 'true');
        }
      }
    } catch (error) {
      console.error('Failed to load subscription from Firestore:', error);
    }

    return userData;
  };

  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      console.warn('Firebase auth check taking too long, showing sign-in button');
      setIsLoading(false);
    }, 3000);

    let unsubscribe;

    const init = async () => {
      // Clear any leftover redirect attempt from the broken redirect rollout
      try {
        await getRedirectResult(auth);
      } catch (redirectError) {
        console.warn('getRedirectResult (cleanup):', redirectError?.code || redirectError);
      }

      try {
        unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          try {
            if (firebaseUser) {
              const userData = await syncBackendAndSubscription(firebaseUser);
              setUser(userData);
              if (onAuthChange) {
                onAuthChange(userData);
              }
            } else {
              setUser(null);
              if (onAuthChange) {
                onAuthChange(null);
              }
            }
          } catch (error) {
            console.error('Error in auth state change handler:', error);
            setUser(null);
          } finally {
            clearTimeout(loadingTimeout);
            setIsLoading(false);
          }
        });
      } catch (error) {
        console.error('Failed to initialize Firebase auth listener:', error);
        clearTimeout(loadingTimeout);
        setIsLoading(false);
        setUser(null);
      }
    };

    init();

    return () => {
      clearTimeout(loadingTimeout);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [onAuthChange]);

  const handleGoogleSignIn = async () => {
    try {
      // Must stay in the user-gesture call stack as much as possible for mobile Safari
      const result = await signInWithPopup(auth, googleProvider);
      const additional = result ? getAdditionalUserInfo(result) : null;
      if (additional?.isNewUser) {
        analytics.trackAuthEvent('sign_up', 'google');
      } else {
        analytics.trackAuthEvent('login', 'google');
      }
      toast.success('Successfully signed in with Google!');
    } catch (error) {
      console.error('Google sign-in error:', error);
      if (error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request') {
        return;
      }
      if (error?.code === 'auth/popup-blocked') {
        toast.error('Pop-up blocked. Allow pop-ups for criticai.ca and try again.');
        return;
      }
      toast.error('Failed to sign in with Google');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      await apiFetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
      });
      // Account balances must not linger for anonymous UI (Math.max with localStorage)
      localStorage.removeItem('criticai_paid_analyses');
      localStorage.removeItem('criticai_paid_analyses_high_water');
      localStorage.removeItem('criticai_paid');
      localStorage.removeItem('criticai_payment_date');
      localStorage.removeItem('criticai_subscription_id');
      localStorage.removeItem('criticai_subscription_customer_id');
      localStorage.removeItem('criticai_subscription_period_end');
      localStorage.removeItem('criticai_subscription');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to log out');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 text-critic-text-muted">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  if (user && user.provider === 'firebase-google') {
    const { initial, gradient } = getUserInitial(user);
    return (
      <div className="flex items-center space-x-2 md:space-x-3">
        <div className="flex items-center space-x-2">
          <div
            className={`w-8 h-8 md:w-8 md:h-8 rounded-full bg-gradient-to-r ${gradient} flex items-center justify-center flex-shrink-0`}
          >
            <span className="text-white text-sm font-bold">{initial}</span>
          </div>
          <span className="text-sm text-white hidden sm:inline">{user.name || user.email}</span>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] p-2.5 md:p-1.5 rounded-lg text-critic-text-muted hover:text-critic-text-primary hover:bg-white/[0.08] active:bg-white/[0.12] transition-all duration-200 touch-manipulation relative z-10"
          title="Logout"
          aria-label="Sign out"
        >
          <LogOut className="h-5 w-5 md:h-4 md:w-4" />
        </button>
      </div>
    );
  }

  const hasSubscription = localStorage.getItem('criticai_subscription') === 'true';

  return (
    <div className="flex flex-col items-center space-y-2 w-full md:w-auto">
      {hasSubscription && (
        <Alert
          tone="warning"
          title="⚠️ Sign In Required"
          className="mb-1 w-full md:w-auto max-w-xs md:max-w-none p-2 md:p-2 text-center"
        >
          Sign in to link your subscription
        </Alert>
      )}
      <button
        id="criticai-google-signin"
        type="button"
        onClick={handleGoogleSignIn}
        className={`flex items-center justify-center space-x-2 px-4 py-2 min-h-[44px] md:min-h-0 rounded-lg md:rounded-2xl transition-all duration-200 font-medium w-full md:w-auto max-w-xs md:max-w-none touch-manipulation active:scale-[0.98] ${
          hasSubscription
            ? 'bg-yellow-500 text-black hover:bg-yellow-400 shadow-lg shadow-yellow-500/50 active:shadow-md'
            : 'bg-white text-neutral-900 hover:bg-neutral-100 active:bg-neutral-200'
        }`}
      >
        <svg className="w-4 h-4 md:w-4 md:h-4 flex-shrink-0" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        <span className="text-sm">Sign in with Google</span>
      </button>
    </div>
  );
};

export default GoogleSignup;
